import BaseController from './base-controller'
import { container } from 'tsyringe'
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Euler,
  Float32BufferAttribute,
  LinearEncoding,
  MathUtils,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Points,
  Quaternion,
  ShaderMaterial,
  Texture,
  Vector2,
  Vector3
} from 'three'
import { MeshSurfaceSampler } from 'three/examples/jsm/math/MeshSurfaceSampler'
import { Body } from 'matter-js'
import {
  BODY_LABEL_ADDON_FROM_SLIT,
  GLOBAL_COIN_SCALE,
  MATTER_TO_THREE_SCALE,
  MODEL_IMPORT_SCALE,
  PhysicsLayerZ,
  SpawnLocation
} from '@/features/experience/misc/constants'
import coinMaterial from '@/features/experience/materials/coin-material'
import gsap, { Sine, Back } from 'gsap'
import { Asset } from '@/state/vault/domain'
import CoinDefinition from './configs/coin-definition'
import physicsFactory from './physics-factory'
import { CollisionGroup } from './rooms/room-physics-base'
import { Howl } from 'howler'
import { Interface } from '@/shared/di/interface'
import type { ISize } from '@/shared/di/interface/size'
import type { IAssets } from '@/shared/di/interface/assets'
import type { ISound } from '@/shared/di/interface/sound'
import { URL_CDN } from '@/common/env'
import Currency from '@/common/currency'
import globalRenderer from '@/shared/misc/global-renderer'
import flareFS from './materials/shaders/coin-flare-fs.glsl'
import flareVS from './materials/shaders/coin-flare-vs.glsl'

let currentParticleMeshCount = 0

export default class CoinController extends BaseController {
  spawnLocation: SpawnLocation
  spinSpeed = 0
  useIdleRotation = false
  useFixedRotation = false
  useRestRotation = false

  #firstUpdate = true
  #zoomInPosition = new Vector3()

  #animatedRotation = new Vector3()
  #definition: CoinDefinition = {} as CoinDefinition

  #randomFactor = Math.random()

  #rotateVector = new Vector2()
  #spinQuaternion = new Quaternion()
  #savedQuaterinion = new Quaternion()
  #spinSound: Howl | undefined
  #zoomInTween: gsap.core.Tween | undefined
  #flareMaterial: ShaderMaterial | undefined

  constructor(asset: Asset, spawnLocation: SpawnLocation) {
    super()
    this.asset = asset
    this.assetId = asset.id
    this.isLoaded = false
    this.spawnLocation = spawnLocation

    this.#randomFactor = Number(this.asset.sn) > 0 ? (Math.sin(Number(this.asset.sn)) + 1) / 2 : Math.random()
  }

  load(): Promise<void> {
    return new Promise(resolve => {
      const assets = container.resolve<IAssets>(Interface.IAssets)

      //get definitions based in asset series
      const definitionsFile = 'series/' + this.asset.c + '/definitions.json'

      assets.loadDefinitions(definitionsFile).then((defs: CoinDefinition[]) => {
        const foundDefinition = defs.find(item => item.denomination === this.asset.d)
        if (foundDefinition) {
          this.#definition = foundDefinition
        } else {
          throw new Error('no coin definition found')
        }

        this.initBody()

        assets.loadSingleCoin(this.#definition).then(() => {
          const model = assets.getModel(this.#definition.modelName) as Object3D
          if (model) {
            this.processCoin(model)
            this.isLoaded = true
          }
          resolve()
        })
      })
    })
  }

  initBody() {
    const shape = this.#definition.physicsShape
    const scale = this.#definition.radius * this.#definition.extraLevelScale * GLOBAL_COIN_SCALE
    if (shape === 'circle') {
      this.body = physicsFactory.createCircle(scale)
    } else if (shape === 'triangle') {
      this.body = physicsFactory.createTriangle(scale)
      this.initAngle = -0.5
    } else if (shape === 'hexagon') {
      this.body = physicsFactory.createHexagon(scale)
      this.initAngle = -0.5
    } else if (shape === 'rhombus') {
      this.body = physicsFactory.createRhombus(scale)
      this.initAngle = -0.25 * Math.PI
    }
    this.body.label = 'coin-' + this.#definition.denomination
    Body.setDensity(this.body, this.#definition.weight)

    super.initBody()
  }

  processCoin(model: Object3D) {
    const coinMesh = model.clone().children.filter((x: Object3D) => x.type === 'Mesh')[0] as Mesh

    const geometry = coinMesh.geometry
    //geometry.deleteAttribute('tangents')
    //delete geometry.attributes.tangent
    //geometry.computeTangents()

    const count = coinMesh.geometry.attributes.position.count

    if (!geometry.attributes.color2) {
      geometry.setAttribute('color2', new BufferAttribute(new Float32Array(count * 3), 3))
    }

    const colors = geometry.attributes.color2
    const color = this.baseColorAsColor //

    for (let i = 0; i < count; i++) colors.setXYZ(i, color.r, color.g, color.b)
    geometry.attributes.color2.needsUpdate = true

    this.mesh = coinMesh
    this.mesh.name = 'model'

    this.meshInitScale = this.#definition.extraLevelScale * MODEL_IMPORT_SCALE * GLOBAL_COIN_SCALE * 0.95

    coinMesh.scale.setScalar(this.meshInitScale)

    coinMesh.rotation.x = Math.PI * 0.5

    coinMesh.castShadow = true
    coinMesh.receiveShadow = true
    coinMesh.userData.enableBloom = globalRenderer.allowBloomOnTokens

    coinMesh.renderOrder = 100

    const materialID =
      'coinMaterial_' + this.#definition.serie.toString() + '_' + this.#definition.denomination.toString()

    const assets = container.resolve<IAssets>(Interface.IAssets)
    let cachedMaterial = assets.getMaterial(materialID)
    if (cachedMaterial === undefined) {
      cachedMaterial = coinMaterial.getMaterial(
        this.#definition.normalMap,
        this.#definition.diffuseMap,
        this.#definition.denomination,
        this.#definition.gemColor
      )
      assets.addMaterial(materialID, cachedMaterial, 'coins')
    }
    coinMesh.material = cachedMaterial

    //used for screenshots
    if (this.asset.ux.user.fixedRotation) {
      this.useFixedRotation = true
    }

    //create glowPoints
    if (currentParticleMeshCount < 15) {
      this.createGlowParticles()
    }
  }

  createGlowParticles() {
    currentParticleMeshCount++

    const assets = container.resolve<IAssets>(Interface.IAssets)
    const sampleGeometry = new BufferGeometry()

    this.#flareMaterial = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      uniforms: {
        uTime: {
          value: Math.random()
        },
        uOpacity: {
          value: 1.0
        },
        tSprite: {
          value: assets.getTexture('flare-coin')
        },
        tEnvMap: {
          value: assets.getTexture('envMap')
        }
      },
      blending: AdditiveBlending,
      vertexShader: flareVS,
      fragmentShader: flareFS
    })

    const position = []
    //const normal = []
    const lifeTime = []
    const startTime = []
    const opacity = []
    const flareColor = []
    const sampler = new MeshSurfaceSampler(this.mesh).setWeightAttribute('color').build()
    const _position = new Vector3()
    const _normal = new Vector3()

    // Sample randomly from the surface, creating an instance of the sample
    // geometry at each sample point.
    for (let i = 0; i < 5; i++) {
      sampler.sample(_position, _normal)
      position.push(_position.x, _position.y, _position.z)
      //normal.push(_normal.x, _normal.y, _normal.z)
      lifeTime.push(7)
      startTime.push((i / 5) * Math.PI * 2 + Math.random())
      opacity.push(0.3 + Math.random() * 0.7)
      flareColor.push(this.baseColorAsColor.r, this.baseColorAsColor.g, this.baseColorAsColor.b)
    }

    sampleGeometry.setAttribute('position', new Float32BufferAttribute(position, 3))
    sampleGeometry.setAttribute('color', new Float32BufferAttribute(flareColor, 3))
    //sampleGeometry.setAttribute('normal', new Float32BufferAttribute(normal, 3))
    sampleGeometry.setAttribute('startTime', new Float32BufferAttribute(startTime, 1))
    sampleGeometry.setAttribute('lifeTime', new Float32BufferAttribute(lifeTime, 1))
    sampleGeometry.setAttribute('opacity', new Float32BufferAttribute(opacity, 1))

    const sampleMesh = new Points(sampleGeometry, this.#flareMaterial)
    sampleMesh.userData.enableBloom = false
    sampleMesh.userData.isParticleSystem = true
    this.mesh.add(sampleMesh)
  }

  onTouchesChanged = (touches: Touch[]) => {
    if (!this.isZoomed || touches.length === 0) {
      this.lastTouchesLength = 0
      return
    }

    if (touches.length === 0) {
      this.currentTouchIdentifier = -1
    }

    //if new, set start position, to get correct delta
    if (this.lastTouchesLength === 0 && touches.length > 0) {
      this.currentTouchIdentifier = touches[0].identifier
      this.previousPointerPosition.x = touches[0].clientX
      this.previousPointerPosition.y = touches[0].clientY
    }

    if (this.currentTouchIdentifier === touches[0].identifier) {
      this.#rotateVector.set(
        touches[0].clientX - this.previousPointerPosition.x,
        touches[0].clientY - this.previousPointerPosition.y
      )

      this.previousPointerPosition.set(touches[0].clientX, touches[0].clientY)
    }

    this.lastTouchesLength = touches.length
  }

  update(time: number, delta: number): void {
    if (!this.isLoaded) {
      return
    }

    if (this.isAnimated) {
      return
    }

    if (this.isZoomed) {
      this.zoomUpdate(time, delta)
      return
    } else {
      super.update(time, delta)
    }

    const bodyAngle = -this.body.angle + this.initAngle
    this.mesh.rotation.y = bodyAngle

    let animRotX = 0
    let animRotZ = 0

    //when landed
    if (this.useRestRotation) {
      animRotX = 0.4 * this.#randomFactor - 0.2
      animRotZ = 0.1 * this.#randomFactor - 0.05
    }

    if (this.useIdleRotation) {
      animRotX += Math.sin(time * 0.06 * Math.PI * 2 - this.#randomFactor * 30) * 0.1
      animRotZ += Math.sin(time * 0.06 * Math.PI * 2 + this.#randomFactor * 30) * 0.1
    }

    if (this.#flareMaterial && !this.isZoomed) {
      this.#flareMaterial.uniforms.uOpacity.value = MathUtils.clamp(animRotZ / 0.08, 0, 1)
    }

    //rest plus idle rotation
    this.#animatedRotation.set(animRotX + Math.PI * 0.5, bodyAngle, animRotZ)

    const fromSlitState = this.body.label.includes(BODY_LABEL_ADDON_FROM_SLIT)

    //when picked up
    if (this.body.collisionFilter.group === CollisionGroup.ActiveCoins || fromSlitState) {
      //iterpolate to neutral
      this.mesh.rotation.x += (Math.PI * 0.5 - this.mesh.rotation.x) * 0.3

      //if animated from slit
      if (!fromSlitState) {
        const velZ = this.mesh.position.z - this.lastPosition.z
        let pickUpAngle = 0
        const size = container.resolve<ISize>(Interface.ISize).getSize()

        if (this.body.position.x < size.width * -0.47) {
          pickUpAngle = velZ * Math.PI
        } else if (this.body.position.x > size.width * 0.47) {
          pickUpAngle = -velZ * Math.PI
        }

        this.mesh.rotation.z += (pickUpAngle - this.mesh.rotation.z) * 0.1
      }
    } else {
      this.mesh.rotation.x += (this.#animatedRotation.x - this.mesh.rotation.x) * (this.#firstUpdate ? 1 : 0.3)
      this.mesh.rotation.z += (this.#animatedRotation.z - this.mesh.rotation.z) * (this.#firstUpdate ? 1 : 0.3)
    }

    if (this.glowSprite) {
      this.mesh.rotation.x += Math.sin(time * 1.0 - this.#randomFactor * 30) * 0.07
      this.mesh.rotation.z += Math.sin(time * 2.0 - this.#randomFactor * 30) * 0.001
      this.mesh.rotation.y += Math.sin(time * 3.0 - this.#randomFactor * 30) * 0.005
    }

    if (this.useFixedRotation) {
      this.mesh.rotation.set(
        Math.PI * 0.5 + this.asset.ux.user.fixedRotation[0],
        this.asset.ux.user.fixedRotation[1],
        this.asset.ux.user.fixedRotation[2]
      )
    }

    if (this.#flareMaterial) {
      this.#flareMaterial.uniforms.uTime.value = time
    }

    this.#firstUpdate = false
  }

  zoomUpdate(time: number, delta: number) {
    const deltaRotationQuaternion = new Quaternion().setFromEuler(
      new Euler(MathUtils.degToRad(this.#rotateVector.y * 1), MathUtils.degToRad(this.#rotateVector.x * 1), 0, 'XYZ')
    )

    this.#rotateVector.multiplyScalar(0.97)

    this.#spinQuaternion.multiplyQuaternions(deltaRotationQuaternion, this.mesh.quaternion)
    this.mesh.position.copy(this.#zoomInPosition)
    this.mesh.quaternion.slerp(this.#spinQuaternion, 0.7)
    this.spinSpeed = this.mesh.quaternion.angleTo(this.#spinQuaternion)

    if (this.#spinSound) {
      const sound = container.resolve<ISound>(Interface.ISound)
      sound.setLoopVolume('drag_2', Math.min(1, this.spinSpeed * 10))
    }
  }

  zoomIn(position: Vector3): Promise<void> {
    const sound = container.resolve<ISound>(Interface.ISound)
    if (this.isZoomed) {
      return Promise.resolve()
    }

    const mat = this.mesh.material as MeshStandardMaterial

    //load high res
    this.isZoomed = true
    this.mesh.userData.enableBloom = true
    this.#spinSound = sound.startLoop('drag_2')
    this.#savedQuaterinion.copy(this.mesh.quaternion)
    this.#spinQuaternion.copy(this.mesh.quaternion)
    this.#rotateVector.set(1, -1)
    //this.disable()
    return new Promise<void>(resolve => {
      this.#zoomInPosition.copy(this.mesh.position)
      this.#zoomInTween = gsap.to(this.#zoomInPosition, {
        x: position.x,
        y: position.y,
        z: position.z,
        duration: 0.3,
        ease: Back.easeOut.config(0.9),
        onComplete: () => {
          //check if zoomOut was triggered before animation complete
          if (this.isZoomed) {
            //this.loadHighRes()
            this.addTouchListener()
          }

          resolve()
        }
      })

      if (this.#flareMaterial) {
        gsap.to(this.#flareMaterial.uniforms.uOpacity, { duration: 0.4, value: 0 })
      }
    })
  }

  zoomOut(updateCallback: () => void, completeCallback: () => void) {
    if (this.#zoomInTween) {
      this.#zoomInTween.kill()
      this.#zoomInTween = undefined
    }

    this.lastTouchesLength = 0
    this.#spinSound?.fade(this.#spinSound.volume(), 0, 300)
    this.removeTouchListener()

    const to = new Vector3(
      this.body.position.x * MATTER_TO_THREE_SCALE,
      -this.body.position.y * MATTER_TO_THREE_SCALE,
      PhysicsLayerZ.Front
    )

    gsap.to(this.#zoomInPosition, {
      x: to.x,
      y: to.y,
      z: to.z,
      duration: 0.5,
      ease: Back.easeIn,
      onUpdate: () => {
        updateCallback()
      },
      onComplete: () => {
        this.#spinSound?.stop()
        completeCallback()
        this.mesh.userData.enableBloom = globalRenderer.allowBloomOnTokens
        this.isZoomed = false
      }
    })

    if (this.#flareMaterial) {
      gsap.to(this.#flareMaterial.uniforms.opacity, { duration: 0.4, value: 1 })
    }

    const qTo = this.#savedQuaterinion

    gsap.to(this.mesh.quaternion, {
      x: qTo.x,
      y: qTo.y,
      z: qTo.z,
      w: qTo.w,
      duration: 0.3,
      ease: Sine.easeInOut
    })
  }

  loadHighRes() {
    if (this.asset.c !== Currency.TJISEK) {
      return
    }

    const assets = container.resolve<IAssets>(Interface.IAssets)
    assets
      .loadTexture(
        'zoomInDiffuseMap' + this.asset.c + this.asset.d,
        `${URL_CDN}/app/assets/series/${this.asset.c.toLowerCase()}/high-res/${this.#definition.diffuseMap}`,
        'coin-zoomin'
      )
      .then((texture: Texture) => {
        ;(this.mesh.material as MeshStandardMaterial).map = texture
      })
    assets
      .loadTexture(
        'zoomInNormalMap' + this.asset.c + this.asset.d,
        `${URL_CDN}/app/assets/series/${this.asset.c.toLowerCase()}/high-res/${this.#definition.normalMap}`,
        'coin-zoomin'
      )
      .then((texture: Texture) => {
        texture.encoding = LinearEncoding
        ;(this.mesh.material as MeshStandardMaterial).normalMap = texture
      })
  }

  unloadHighRes() {
    const mat = this.mesh.material as MeshStandardMaterial
    const assets = container.resolve<IAssets>(Interface.IAssets)
    mat.normalMap = assets.getTexture(this.#definition.normalMap)
    mat.map = assets.getTexture(this.#definition.diffuseMap)
    assets.unloadNamespace('coin-zoomin')
  }

  destroy() {
    super.destroy()
    currentParticleMeshCount--
  }

  get denomination() {
    return this.#definition.denomination
  }

  get baseColor() {
    return this.#definition.baseColor
  }

  get baseColorAsColor() {
    return new Color(this.#definition.baseColor)
  }

  get valueInCents() {
    return this.denomination as number
  }

  get definition() {
    return this.#definition
  }
}
